// Centralized route configuration — single source of truth for all URL mappings

export type PageId = "finance" | "home" | "firsthome" | "downsizing" | "tools" | "blog" | "article" | "rentroll" | "rentrollfinance" | "newsletter" | "edition" | "complaints" | "websitepolicy" | "about" | "notfound" | "services" | "advisoryrentroll" | "commercial" | "property" | "insights" | "contact" | "local";

export const ROUTES = {
  finance: "/",
  local: "/southern-highlands-finance",
  services: "/services",
  advisoryrentroll: "/rent-roll-finance",
  commercial: "/commercial-property-lending",
  property: "/services/property-residential-finance",
  insights: "/insights",
  contact: "/contact",
  home: "/home",
  firsthome: "/first-home-buyer",
  downsizing: "/downsizing",
  blog: "/blog",
  rentroll: "/rent-roll-acquisition",
  rentrollfinance: "/rent-roll-finance",
  newsletter: "/intelligence",
  complaints: "/feedback-and-complaints",
  websitepolicy: "/website-policy",
  about: "/about",
} as const;

export const BASE_URL = "https://www.pendiumfinance.com.au";

// Reverse lookup: path → PageId
const PATH_TO_PAGE: Record<string, PageId> = {
  "/": "finance",
  "/southern-highlands-finance": "local",
  "/services": "services",
  "/services/rent-roll-finance": "advisoryrentroll",
  "/services/commercial-business-finance": "commercial",
  "/commercial-property-lending": "commercial",
  "/services/property-residential-finance": "property",
  "/insights": "insights",
  "/contact": "contact",
  "/home": "home",
  "/first-home-buyer": "firsthome",
  "/downsizing": "downsizing",
  "/tools": "rentrollfinance",
  "/blog": "blog",
  "/rent-roll-acquisition": "rentroll",
  "/rent-roll-finance": "rentrollfinance",
  "/intelligence": "newsletter",
  "/feedback-and-complaints": "complaints",
  "/website-policy": "websitepolicy",
  "/about": "about",
};

export function pathToPageId(pathname: string): PageId {
  if (pathname.startsWith("/blog/") && pathname.length > 6) return "article";
  // Monthly Brief editions. These were static HTML under public/intelligence/
  // until they moved into the app; the URLs are unchanged so existing links,
  // shares and any indexed copies keep resolving.
  if (pathname.startsWith("/intelligence/") && pathname.length > 14) return "edition";
  return PATH_TO_PAGE[pathname.replace(/\/$/, "") || "/"] ?? "notfound";
}

export function pageIdToPath(page: PageId): string {
  if (page === "article" || page === "notfound") return "/";
  if (page === "edition") return ROUTES.newsletter;
  if (page === "tools") return ROUTES.rentrollfinance; // suite consolidated into /rent-roll-finance
  return ROUTES[page] ?? "/";
}

/* Scroll reset is DEFERRED, not skipped.
   Every navigate*() below used to call window.scrollTo(0, 0) inline, which ran
   while AnimatePresence still had the OUTGOING page mounted and visible: from
   scrollY 2500 the page snapped to its top at t=51ms and only dissolved 366ms
   later, so a deliberate transition read as a glitch. These helpers now only
   RECORD that a reset is owed; PageTransition performs it once the outgoing
   page has finished exiting (see consumeScrollReset).
   A real back/forward press never sets the flag, so browser scroll restoration
   keeps working — which is why this can't simply live in the popstate handler,
   given navigateTo dispatches a synthetic popstate of its own. */
let scrollResetPending = false;

/** True once, if a programmatic navigation since the last call owed a scroll reset. */
export function consumeScrollReset(): boolean {
  const owed = scrollResetPending;
  scrollResetPending = false;
  return owed;
}

export function navigateTo(page: PageId): void {
  const path = pageIdToPath(page);
  scrollResetPending = true;
  window.history.pushState({ page }, "", path);
  // Dispatch popstate so the App listener picks it up
  window.dispatchEvent(new PopStateEvent("popstate", { state: { page } }));
}

/** Navigate to a section on another page without reloading the document. */
export function navigateToHash(page: PageId, hash: string): void {
  scrollResetPending = true;
  window.history.pushState({ page }, "", pageIdToPath(page) + hash);
  window.dispatchEvent(new PopStateEvent("popstate", { state: { page } }));
}

export function getArticleSlug(): string | null {
  const match = window.location.pathname.match(/^\/blog\/(.+)$/);
  if (!match) return null;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
}

/** The slug of the Monthly Brief edition in the current URL, if any. */
export function getEditionSlug(): string | null {
  const match = window.location.pathname.match(/^\/intelligence\/(.+)$/);
  if (!match) return null;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
}

/** Navigate to a Monthly Brief edition by slug */
export function navigateToEdition(slug: string): void {
  const path = `/intelligence/${encodeURIComponent(slug)}`;
  scrollResetPending = true;
  window.history.pushState({ page: "edition", slug }, "", path);
  window.dispatchEvent(new PopStateEvent("popstate", { state: { page: "edition", slug } }));
}

/** Navigate to a blog article by slug */
export function navigateToArticle(slug: string): void {
  const path = `/blog/${encodeURIComponent(slug)}`;
  scrollResetPending = true;
  window.history.pushState({ page: "article", slug }, "", path);
  window.dispatchEvent(new PopStateEvent("popstate", { state: { page: "article", slug } }));
}

// Legacy hash → path redirect map (for bookmarks/shared links from the old hash router)
const HASH_TO_PATH: Record<string, string> = {
  "#home": ROUTES.home,
  "#firsthome": ROUTES.firsthome,
  "#tools": ROUTES.rentrollfinance,
};

/** Call once on app init to redirect legacy hash URLs */
export function redirectLegacyHash(): void {
  const hash = window.location.hash;
  const path = HASH_TO_PATH[hash];
  if (path) {
    window.history.replaceState(null, "", path);
  }
}
